<template>
  <v-stepper v-model="current_step" non-linear data-testid="regimen_exercise_meta_edit">
    <v-stepper-header>
      <v-stepper-step :complete="current_step > 1" step="1">
        {{ $t("common.select_exercise") }}
      </v-stepper-step>
      <v-divider />

      <v-stepper-step :complete="current_step > 2" step="2">
        {{ $t("regimen_exercise_meta_edit.configure_exercise") }}
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <v-container style="min-height: 700px">
          <p>
            {{ $t("regimen_exercise_meta_edit.select_type_exercise_text") }}
          </p>

          <v-layout>
            <v-flex md4>
              <CombinedSelectExercise
                v-if="is_exercise_type_allowed('interactive') || is_exercise_type_allowed('video')"
                v-model="localValue.exercise_id"
                prepend_icon="category"
                :label="$t('common.select_exercise')"
                :allowed_types="combined_select_allowed_types"
                show_filters
                @change="combined_exercise_change"
              />

              <v-autocomplete
                v-if="grid_selection_available"
                data-testid="regimen_exercise_meta_edit_select_grid"
                v-model="localValue.exercise_id"
                :items="grid_options"
                :filter="grid_filter"
                prepend-icon="fas fa-border-all"
                outlined
                :label="$t('clinic_page.grids')"
                @change="grid_change"
              >
                <template v-slot:item="data">
                  <template v-if="typeof data.item !== 'object'">
                    <v-list-item-content v-text="data.item" />
                  </template>
                  <template v-else>
                    <v-list-item-avatar>
                      <v-icon>{{ data.item.icon }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title v-html="data.item.text" />
                      <v-list-item-subtitle v-html="data.item.subtitle" />
                    </v-list-item-content>
                  </template>
                </template>
              </v-autocomplete>

              <v-select
                v-model="select_other"
                prepend-icon="view_headline"
                :items="select_other_options"
                outlined
                :label="$t('regimen_exercise_meta_edit.other_misc')"
                data-testid="regimen_exercise_meta_edit_other_misc"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title v-html="item.text" />
                    <v-list-item-subtitle v-html="item.subtitle" />
                  </v-list-item-content>
                </template>
              </v-select>
            </v-flex>
          </v-layout>
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="2">
        <div style="min-height: 400px">
          <RegimenExerciseEdit v-if="exercises && localValue.exercise_type == 'interactive'" v-model="localValue" @save="save" />

          <RegimenVideoEdit
            v-if="video_exercises && (localValue.exercise_type == 'video/clinic' || localValue.exercise_type == 'video/builtin' || localValue.exercise_type == 'video/public')"
            v-model="localValue"
            @save="save"
          />

          <CustomExerciseEdit v-if="localValue.exercise_type == 'custom'" v-model="localValue" @save="save" />

          <RegimenHeaderEdit v-if="localValue.exercise_type == 'header'" v-model="localValue" @save="save" />

          <RegimenQuestionnaireEdit v-if="localValue.exercise_type == 'questionnaire'" v-model="localValue" @save="save" />

          <RegimenQuestionEdit v-if="localValue.exercise_type == 'question'" v-model="localValue" @save="save" />

          <RegimenAssessmentEdit v-if="localValue.exercise_type == 'assessment'" v-model="localValue" @save="save" />

          <RegimenEmailEdit v-if="localValue.exercise_type == 'email'" v-model="localValue" @save="save" />

          <RegimenGridSequenceEdit v-if="localValue.exercise_type == 'grid_sequence'" v-model="localValue" @save="save" />

          <RegimenGridSetEdit v-if="localValue.exercise_type == 'grid_set'" v-model="localValue" @save="save" />

          <RegimenGridScheduleEdit v-if="localValue.exercise_type == 'grid_schedule'" v-model="localValue" @save="save" />
        </div>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { getExerciseDefaultConfig } from "../classes/exercise";
import RegimenExerciseEdit from "./RegimenExerciseEdit";
import CustomExerciseEdit from "./CustomExerciseEdit";
import RegimenVideoEdit from "./RegimenVideoEdit";
import RegimenHeaderEdit from "./RegimenHeaderEdit";
import RegimenQuestionnaireEdit from "./RegimenQuestionnaireEdit";
import RegimenQuestionEdit from "./RegimenQuestionEdit";
import RegimenAssessmentEdit from "./RegimenAssessmentEdit";
import RegimenEmailEdit from "./RegimenEmailEdit";
import RegimenGridSequenceEdit from "./RegimenGridSequenceEdit";
import RegimenGridSetEdit from "./RegimenGridSetEdit.vue";
import RegimenGridScheduleEdit from "./RegimenGridScheduleEdit.vue";
import CombinedSelectExercise from "./CombinedSelectExercise";

import Vue from "vue";

import { mapState } from "vuex";

export default {
  components: {
    RegimenExerciseEdit,
    CustomExerciseEdit,
    RegimenVideoEdit,
    RegimenHeaderEdit,
    RegimenQuestionnaireEdit,
    RegimenQuestionEdit,
    RegimenAssessmentEdit,
    RegimenEmailEdit,
    RegimenGridSequenceEdit,
    RegimenGridSetEdit,
    RegimenGridScheduleEdit,
    CombinedSelectExercise,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    context: {
      type: String,
      required: true,
    },
    allowed_exercise_types: {
      type: Array,
      default: () => ["interactive", "video", "custom", "header", "question", "questionnaire", "grid_sequence", "grid_set", "grid_schedule"],
    },
    assigned_exercises: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    var current_step;
    if (this.value.exercise_type == "none" || !this.value.exercise_type) {
      current_step = 1;
    } else {
      current_step = 2;
    }

    let data = {
      exercise_type: this.value.exercise_type || "none", // [none, interactive, video/clinic, video/builtin, video/public, custom]
      errors: [],
      current_step,
    };

    // TODO: Remove this at some point in the future
    // Handle old data-format where we didn't have this.value.exercise_type defined and everything was implicitly 'interactive'.
    if (data.exercise_type == "none" && this.value.exercise_id) {
      data.exercise_type = "interactive";
    }

    // Select Other
    data.select_other = "";
    return data;
  },
  computed: {
    ...mapState(["exercises", "video_exercises", "grid_sets", "grid_schedules", "grid_sequences", "app_config"]),
    localValue: {
      get() {
        return this.value;
      },
      set(localValue) {
        // TODO: Remove this at some point in the future
        // Handle old data-format where we didn't have value.exercise_type defined and everything was implicitly 'interactive'.
        if ((!localValue.exercise_type || localValue.exercise_type == "none") && this.value.exercise_id) {
          localValue.exercise_type = "interactive";
        }

        this.$emit("input", localValue);
      },
    },
    combined_select_allowed_types() {
      return ["interactive", "video"].filter((type) =>
        this.is_exercise_type_allowed(type)
      );
    },
    grid_selection_available() {
      return this.grid_options.some((item) => item.value);
    },
    grid_options() {
      if (this.context !== "regimen") {
        return [];
      }

      const groups = [];

      if (
        this.app_config.exercise_types.includes("grid_sequence")
        && this.is_exercise_type_allowed("grid_sequence")
      ) {
        const sequences = (this.grid_sequences || [])
          .filter((sequence) => !this.source_already_assigned("grid_sequence", sequence.id))
          .map((sequence) => ({
            text: sequence.title,
            subtitle: sequence.description,
            value: sequence.id,
            exercise_type: "grid_sequence",
            icon: "mdi-source-branch",
          }));
        if (sequences.length > 0) {
          groups.push({
            header: this.$t("community_sharing.grid_sequences"),
            items: sequences,
          });
        }
      }

      if (
        this.app_config.exercise_types.includes("grid_set")
        && this.is_exercise_type_allowed("grid_set")
      ) {
        const sets = (this.grid_sets || [])
          .filter((gridSet) => !this.source_already_assigned("grid_set", gridSet.id))
          .map((gridSet) => ({
            text: gridSet.title,
            subtitle: gridSet.description,
            value: gridSet.id,
            exercise_type: "grid_set",
            icon: "fas fa-align-justify",
          }));
        if (sets.length > 0) {
          groups.push({
            header: this.$t("community_sharing.grid_sets"),
            items: sets,
          });
        }
      }

      if (
        this.app_config.exercise_types.includes("grid_schedule")
        && this.is_exercise_type_allowed("grid_schedule")
      ) {
        const schedules = (this.grid_schedules || [])
          .filter((schedule) => !this.source_already_assigned("grid_schedule", schedule.id))
          .map((schedule) => ({
            text: schedule.title,
            subtitle: schedule.description,
            value: schedule.id,
            exercise_type: "grid_schedule",
            icon: "fas fa-border-all",
          }));
        if (schedules.length > 0) {
          groups.push({
            header: this.$t("community_sharing.grid_schedules"),
            items: schedules,
          });
        }
      }

      const options = [];
      for (let i = 0; i < groups.length; i++) {
        if (i !== 0) {
          options.push({ divider: true });
        }
        options.push({ header: groups[i].header });
        options.push(...groups[i].items);
      }
      return options;
    },

    select_other_options() {
      let select_other_options = [
        { text: this.$t("regimen_exercise_meta_edit.other_misc"), value: "" },
        {
          text: this.$t("regimen_exercise_meta_edit.one_off"),
          subtitle: this.$t("regimen_exercise_meta_edit.one_off_desc"),
          value: "custom",
        },
        {
          text: this.$t("regimen_exercise_meta_edit.section_header"),
          subtitle: this.$t("regimen_exercise_meta_edit.section_header_desc"),
          value: "header",
        },
        {
          text: this.$t("regimen_exercise_meta_edit.single_question"),
          subtitle: this.$t("regimen_exercise_meta_edit.single_question_subheader"),
          value: "question",
        },
        {
          text: this.$t("regimen_exercise_meta_edit.questionnaire"),
          subtitle: this.$t("regimen_exercise_meta_edit.questionnaire_subheader"),
          value: "questionnaire",
        },
        {
          text: this.$t("common.assessment"),
          subtitle: this.$t("regimen_exercise_meta_edit.assessment_subheader"),
          value: "assessment",
        },
      ];

      // Emails are valid in scheduled and report-driven sequences.
      if (this.context == "grid_schedule" || this.context == "grid_sequence") {
        select_other_options.push({
          text: this.$t("common.email"),
          subtitle: this.$t("regimen_exercise_meta_edit.email_desc"),
          value: "email",
        });
      }

      // Filter select_other_options by what is enabled in the app_config.exercises
      select_other_options = select_other_options.filter((option) => {
        return this.app_config.exercise_types.includes(option.value);
      });
      select_other_options = select_other_options.filter((option) => this.is_exercise_type_allowed(option.value));
      if (this.context == "grid_sequence") {
        select_other_options = select_other_options.filter((option) => option.value !== "header");
      }

      return select_other_options;
    },
  },
  watch: {
    select_other(val) {
      if (val == "custom") {
        this.localValue.exercise_type = "custom";
        this.localValue.exercise_id = null;
        this.current_step = 2;
      }
      if (val == "header") {
        this.localValue.exercise_type = "header";
        this.localValue.exercise_id = null;
        this.current_step = 2;
      }
      if (val == "question") {
        this.localValue.exercise_type = "question";
        this.localValue.exercise_id = null;
        this.current_step = 2;
      }
      if (val == "questionnaire") {
        this.localValue.exercise_type = "questionnaire";
        this.localValue.exercise_id = null;
        this.current_step = 2;
      }
      if (val == "assessment") {
        this.localValue.exercise_type = "assessment";
        this.localValue.exercise_id = null;
        this.current_step = 2;
      }
      if (val == "email") {
        this.localValue.exercise_type = "email";
        this.localValue.exercise_id = null;
        this.current_step = 2;
      }
      Vue.nextTick(() => (this.select_other = ""));
    },
    localValue() {
      if (this.value.exercise_type == "none" || !this.value.exercise_type) {
        this.current_step = 1;
      } else {
        this.current_step = 2;
      }
    },
  },
  methods: {
    is_exercise_type_allowed(exercise_type) {
      return this.allowed_exercise_types.includes(exercise_type);
    },
    source_already_assigned(exerciseType, exerciseId) {
      return this.assigned_exercises.some((exercise) =>
        exercise.exercise_type === exerciseType
        && exercise.exercise_id === exerciseId
        && exercise.uuid !== this.localValue.uuid);
    },
    combined_exercise_change(selected) {
      if (!selected || !selected.value) {
        return;
      }
      if (selected.exercise_type === "interactive") {
        this.exercise_change();
      } else if (selected.exercise_type === "video") {
        this.video_change();
      }
    },
    // We've selected an interactive exercise
    exercise_change() {
      this.localValue.exercise_type = "interactive";

      // On exercise change, we replace the config wholesale
      // TODO: Leave this for RegimenExerciseEdit to handle ??
      let exercise = this.exercises[this.localValue.exercise_id];
      this.localValue.config = getExerciseDefaultConfig(exercise, window.OpticalGymApp);
      if (exercise.num_levels > 1) {
        this.localValue.level = 1;
        this.localValue.max_level = exercise.num_levels;
      } else {
        this.localValue.level = 0;
        this.localValue.max_level = 0;
      }

      this.localValue.config_version = exercise.config_version;

      this.current_step = 2;
    },

    // We've selected a video exercise
    video_change() {
      let exercise_id = this.localValue.exercise_id;

      if (!exercise_id) {
        return;
      }

      let video = this.video_exercises[exercise_id];
      this.localValue.exercise_type = "video/" + video.origin;

      // TODO - configure it in step 2
      this.current_step = 2;
    },
    grid_filter(item, queryText, _displayTitle) {
      if (item.header || item.divider) {
        return false;
      }
      const filter = queryText.trim().toLowerCase();
      const title = (item.text || "").trim().toLowerCase();
      const subtitle = (item.subtitle || "").trim().toLowerCase();
      return title.includes(filter) || subtitle.includes(filter);
    },
    grid_change(exercise_id) {
      if (!exercise_id) {
        return;
      }
      const selected = this.grid_options.find(
        (item) => item.value === exercise_id && item.exercise_type
      );
      if (!selected) {
        return;
      }
      if (selected.exercise_type === "grid_sequence") {
        this.grid_sequence_change(exercise_id);
      } else if (selected.exercise_type === "grid_set") {
        this.grid_set_change(exercise_id);
      } else if (selected.exercise_type === "grid_schedule") {
        this.grid_schedule_change(exercise_id);
      }
    },
    grid_sequence_change(exercise_id) {
      if (!exercise_id) {
        return;
      }
      let sequence = this.grid_sequences.find((grid_sequence) => grid_sequence.id === exercise_id);
      if (
        !sequence
        || sequence.sequence.length === 0
        || this.source_already_assigned("grid_sequence", exercise_id)
      ) {
        return;
      }
      this.localValue.exercise_type = "grid_sequence";
      Vue.set(this.localValue, "grid_sequence", JSON.parse(JSON.stringify(sequence)));
      Vue.set(this.localValue, "grid_customized", false);
      Vue.set(this.localValue, "active_step", sequence.sequence[0].id);
      this.current_step = 2;
    },
    grid_set_change(exercise_id) {
      const gridSet = (this.grid_sets || []).find((candidate) => candidate.id === exercise_id);
      if (!gridSet || this.source_already_assigned("grid_set", exercise_id)) return;
      this.localValue.exercise_type = "grid_set";
      Vue.set(this.localValue, "grid_set", JSON.parse(JSON.stringify(gridSet)));
      Vue.set(this.localValue, "grid_customized", false);
      this.current_step = 2;
    },
    grid_schedule_change(exercise_id) {
      const schedule = (this.grid_schedules || []).find((candidate) => candidate.id === exercise_id);
      if (
        !schedule
        || schedule.grid.length === 0
        || this.source_already_assigned("grid_schedule", exercise_id)
      ) {
        return;
      }
      this.localValue.exercise_type = "grid_schedule";
      Vue.set(this.localValue, "grid_schedule", JSON.parse(JSON.stringify(schedule)));
      Vue.set(this.localValue, "grid_customized", false);
      Vue.set(this.localValue, "grid_current_stage", schedule.grid[0].id);
      Vue.set(this.localValue, "grid_next_stage_date", null);
      this.current_step = 2;
    },

    save() {
      this.$emit("save", this.localValue);
    },
  },
};
</script>

<style></style>
